/**
 * ExecutionDetail Page
 *
 * Real-time execution monitoring with unified trace view:
 * - Progressive disclosure: Steps > Activities > Details
 * - Sticky header with status, progress, and stats
 * - Filter chips for activity types + search
 * - Auto-grouping by plan steps or LLM turns
 * - Agent filter for multi-agent executions
 * - Stop/restart controls
 */

import { A, useParams, useNavigate } from '@solidjs/router';
import {
  type Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  Show,
} from 'solid-js';
import {
  cancelExecution,
  fetchExecution,
  restartExecution,
  fetchAgents,
  fetchProjects,
} from '../services/api.ts';
import { createExecutionStore } from '../stores/executionStore.ts';
import { TraceView } from '../components/Execution/index.ts';

const ExecutionDetail: Component = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Create execution store
  const store = createExecutionStore();

  // UI state
  const [showCancelDialog, setShowCancelDialog] = createSignal(false);
  const [isCancelling, setIsCancelling] = createSignal(false);
  const [isRestarting, setIsRestarting] = createSignal(false);

  // Fetch execution details
  const [execution, { refetch }] = createResource(
    () => params.id,
    (id) => {
      if (!id) return null;
      return fetchExecution(id);
    },
  );

  // Fetch projects and agents for display names
  const [projectsData] = createResource(() => fetchProjects({ limit: 100 }));
  const [agentsData] = createResource(() => fetchAgents({ limit: 100 }));

  // Subscribe to execution events when ID changes
  createEffect(() => {
    const id = params.id;
    if (id) {
      store.subscribe(id);
    }
  });

  // Update execution object in store when it loads
  createEffect(() => {
    const id = params.id;
    const exec = execution();
    if (id && exec) {
      store.subscribe(id, exec);
    }
  });

  // Refetch execution details when a terminal event is received
  createEffect(() => {
    const currentPhase = store.phase();
    const isTerminal = currentPhase === 'completed' || currentPhase === 'failed' || currentPhase === 'cancelled';

    if (isTerminal) {
      refetch();
    }
  });

  // Cleanup on unmount
  onCleanup(() => {
    store.unsubscribe();
  });

  // Helper functions
  const getProjectName = (projectId?: string) => {
    if (!projectId) return null;
    const project = projectsData()?.items.find((p) => p.id === projectId);
    return project?.name;
  };

  const getAgentName = (agentId?: string) => {
    if (!agentId) return null;
    const agent = agentsData()?.items.find((a) => a.id === agentId);
    return agent?.name;
  };

  // Compute back URL - go to project's executions tab if we have a projectId
  const backUrl = createMemo(() => {
    const projectId = execution()?.projectId;
    if (projectId) {
      return `/projects/${projectId}?tab=executions`;
    }
    return '/executions';
  });

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  // Actions
  const handleCancelExecution = async () => {
    if (!params.id) return;

    setIsCancelling(true);
    try {
      await cancelExecution(params.id);
      refetch();
    } catch (error) {
      console.error('Failed to cancel execution:', error);
    } finally {
      setIsCancelling(false);
      setShowCancelDialog(false);
    }
  };

  const handleRestartExecution = async () => {
    if (!params.id) return;

    setIsRestarting(true);
    try {
      const result = await restartExecution(params.id);
      navigate(`/executions/${result.newExecutionId}`);
    } catch (error) {
      console.error('Failed to restart execution:', error);
    } finally {
      setIsRestarting(false);
    }
  };

  // Connection status indicator
  const connectionIndicator = createMemo(() => {
    const state = store.state.connectionState();
    switch (state) {
      case 'connected':
        return { color: 'bg-green-400', label: 'Live' };
      case 'connecting':
      case 'reconnecting':
        return { color: 'bg-yellow-400 animate-pulse', label: 'Connecting...' };
      default:
        return { color: 'bg-gray-400', label: 'Disconnected' };
    }
  });

  // Check if execution is active
  const isActive = createMemo(() => {
    const exec = execution();
    return exec?.status === 'running' || exec?.status === 'pending';
  });

  const canRestart = createMemo(() => {
    const exec = execution();
    return exec?.status === 'failed' || exec?.status === 'cancelled';
  });

  const isLive = createMemo(() => {
    return store.state.connectionState() === 'connected' && isActive();
  });

  return (
    <div>
      {/* Cancel Confirmation Dialog */}
      <Show when={showCancelDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 rounded-lg p-6 max-w-md text-center border border-gray-700">
            <h2 class="text-xl font-medium mb-4">Cancel Execution</h2>
            <p class="text-gray-400 mb-6">
              Are you sure you want to cancel this execution? This action cannot be undone.
            </p>
            <div class="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setShowCancelDialog(false)}
                disabled={isCancelling()}
                class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-50"
              >
                Keep Running
              </button>
              <button
                type="button"
                onClick={handleCancelExecution}
                disabled={isCancelling()}
                class="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50"
              >
                {isCancelling() ? 'Cancelling...' : 'Cancel Execution'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Header */}
      <div class="mb-6">
        <div class="flex items-center gap-4 mb-4">
          <A
            href={backUrl()}
            class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </A>

          <div class="flex-1">
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              Execution
              <Show when={isActive()}>
                <span class="flex items-center gap-2 text-sm">
                  <span class={`w-2 h-2 rounded-full ${connectionIndicator().color}`} />
                  <span class="text-gray-400 font-normal">{connectionIndicator().label}</span>
                </span>
              </Show>
            </h1>
            <Show when={execution()}>
              <div class="flex items-center gap-2 mt-1">
                <Show when={execution()?.projectId}>
                  <A
                    href={`/projects/${execution()!.projectId}`}
                    class="text-sm text-purple-400 hover:text-purple-300"
                  >
                    {getProjectName(execution()!.projectId) || 'Project'}
                  </A>
                </Show>
                <Show when={execution()?.agentId}>
                  <span class="text-gray-600">|</span>
                  <span class="text-sm text-gray-400">
                    {getAgentName(execution()!.agentId) || 'Agent'}
                  </span>
                </Show>
              </div>
            </Show>
          </div>

          {/* Action buttons */}
          <div class="flex items-center gap-2">
            <Show when={canRestart()}>
              <button
                type="button"
                onClick={handleRestartExecution}
                disabled={isRestarting()}
                class="px-4 py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium transition-colors disabled:opacity-50"
              >
                {isRestarting() ? 'Restarting...' : 'Restart'}
              </button>
            </Show>
            <Show when={isActive()}>
              <button
                type="button"
                onClick={() => setShowCancelDialog(true)}
                class="px-4 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 font-medium transition-colors"
              >
                Cancel
              </button>
            </Show>
          </div>
        </div>
      </div>

      {/* Unified Trace View */}
      <Show when={!store.state.isLoading() || store.state.events().length > 0}>
        <TraceView
          events={store.filteredEvents()}
          agents={store.agents()}
          planSteps={store.planSteps()}
          stats={store.stats()}
          progress={store.progress()}
          phase={store.phase()}
          status={execution()?.status || 'pending'}
          agentName={getAgentName(execution()?.agentId) || undefined}
          isLive={isLive()}
          selectedAgentId={store.state.agentIdFilter()}
          onAgentSelect={store.setAgentFilter}
          formatTime={formatTime}
          formatDuration={formatDuration}
          error={execution()?.error}
        />
      </Show>

      {/* Loading state */}
      <Show when={store.state.isLoading() && store.state.events().length === 0}>
        <div class="bg-gray-800/50 rounded-lg border border-gray-700 p-8 text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4" />
          <p class="text-gray-400">Loading execution data...</p>
        </div>
      </Show>
    </div>
  );
};

export default ExecutionDetail;
